import Penpal, { IConnectionObject } from 'penpal';
// Import Config.
import { CONFIG_PENPAL_CONNECTION_TIMEOUT, CONFIG_ADITION } from '../../config';
// Import Interfaces.
import { PermissionCore } from '../interfaces';
import { PermissionModel, PermissionData, LayerParameters, AditionClientConfig } from '../../interfaces';
import { PermissionClientMessaging } from '../../client/tools/interfaces';
// Import Services.
import * as Logger from '../../common/tools/logger';
import { AditionService } from './adition.service';
import { PermissionLayerOptions } from '../../layer/interfaces';
import { Dimensions } from '../../client/interfaces';

/**
 * @class
 * @name CommunicationHandlerService
 * @description Function to handle the communication between the components (Permission Layer, Core and Publisher Page).
 */
export class CommunicationHandlerService {

  /**
   * @var {string} moduleName Reflects the modules name.
   */
  private moduleName = `CommunicationHandlerService`;

  private aditionService: AditionService = new AditionService();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private parentConnection: IConnectionObject<any>;

  /**
   * Permission Client (parent) API exposed through Penpal
   */
  private permissionClient: PermissionClientMessaging;

  /**
   * The currently active set of permissions. This will be updated as the user grants/revokes permissions
   */
  public permissionModel: PermissionModel;

  /**
   * @public
   * @async
   * @function initialize
   * @description Function to initialize a post message listener to handle commands.
   * @return {Promise<void>}
   */
  public async initialize(): Promise<void> {

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return new Promise<void>(async (resolve: () => void) => {

      const parentApi: PermissionCore = {
        initializePermissionLayer: async (permissionModel: PermissionModel) => {
          Logger.info(this.moduleName, `initializePermissionLayer`, 'parent called initializePermissionLayer', permissionModel);
          this.permissionModel = permissionModel;
          await this.aditionService.initialize(this.getPermissionStates(permissionModel), this);
        }
      };

      this.parentConnection = Penpal.connectToParent({
        // Methods child is exposing to parent
        methods: parentApi,
        timeout: CONFIG_PENPAL_CONNECTION_TIMEOUT,
      });
      this.permissionClient = await this.parentConnection.promise as PermissionClientMessaging;

      Logger.debug(this.moduleName, `initialize`, `Module initialized.`);

      // Resolve.
      return resolve();
    });
  }

  /**
   * @description Function to get the permission states and wrap them into a string for the ADITION request.
   * @return {string}
   * @param permissionModel
   */
  public getPermissionStates(permissionModel: PermissionModel): string {
    let permissionStates = '';

    // No `permissionData`.
    if (!permissionModel || !permissionModel.data || permissionModel.data.length === 0) return permissionStates;

    for (let i = 0; i < permissionModel.data.length; i++) {
      const currentPermission: PermissionData = permissionModel.data[i];

      if (currentPermission.usecase && currentPermission.status) {
        permissionStates = `${permissionStates}&prf[${CONFIG_ADITION.PRF_PARAM_PREFIX.CONSENT}_${currentPermission.usecase}]=${currentPermission.status === 'VALID' ? 1 : 0}`;
      }
    }

    return permissionStates;
  }

  /**
   * Forwards the command from the Permission Layer (Adition Iframe) to the Permission Client (Integration Library)
   */
  public openPermissionLayer(options?: PermissionLayerOptions): void {
    return this.permissionClient.openPermissionLayer(options);
  }

  /**
   * Forwards the command from the Permission Layer (Adition Iframe) to the Permission Client (Integration Library)
   */
  public closePermissionLayer(): void {
    this.permissionClient.closePermissionLayer();
    this.destroy();
  }

  /**
   * Forwards the command from the Permission Layer (Adition Iframe) to the Permission Client (Integration Library)
   */
  public setPermissions(permissionData: PermissionData[]): void {
    permissionData.forEach((permission) => {
      if (permission.usecase && permission.status) {
        this.updatePermissionModel(this.permissionModel, permission);
      }
    });
    this.permissionClient.setPermissions(this.permissionModel);
  }

  /**
   * Forwards the command from the Permission Layer (Adition Iframe) to the Permission Client (Integration Library)
   */
  public updatePermissionLayer(): void {
    this.aditionService.initialize(this.getPermissionStates(this.permissionModel), this);
  }

  public resizePermissionLayer(dimensions: Dimensions): void {
    this.permissionClient.resizePermissionLayer(dimensions);
  }

  /**
   * @public
   * @async
   * @function getAditionClientConfig
   * @description Function to get the required ADITION config, set by the Permission Client.
   * @return {string}
   */
  public async getAditionClientConfig(): Promise<AditionClientConfig> {
    return this.permissionClient.getAditionClientConfig();
  }

  /**
   * @public
   * @async
   * @description Function to get custom consent parameters from the publisher page.
   * @return {Promise<object>}
   */
  public async getLayerParameters(): Promise<LayerParameters> {
    return this.permissionClient.getParameters();
  }

  /**
   * @description Function to set the permission data for a single `permissionType`.
   * @param {PermissionModel} permissionModel The model to update
   * @param {PermissionData} permissionData The permission data, which needs to be set.
   * @return {Promise<void>
   */
  public updatePermissionModel(permissionModel: PermissionModel, permissionData: PermissionData): PermissionModel {

    // No permission model
    if (!permissionModel) {
      Logger.error(this.moduleName, `updatePermissionModel`, `No permission model to update.`);
      return null;
    }

    // No permission data
    if (!permissionData) {
      Logger.error(this.moduleName, `updatePermissionModel`, `Got no permission data to save.`);
      return null;
    }

    try {
      // Get local stored permission data.
      const localPermissionData = permissionModel.data;

      // No `localPermissionData` stored.
      if (localPermissionData.length === 0) {
        localPermissionData.push(permissionData);

        Logger.info(this.moduleName, `updatePermissionModel`, `Successfully set the first permission data entry for permissionType '${permissionData.usecase}'.`, permissionData);
      } else {

        // Reflects, if a permission is locally available.
        let permissionAvailable = false;

        // Loop `localPermissionData`.
        for (let i = 0; i < localPermissionData.length; i++) {

          // If the `permissionData`'s `permissionType` matches the local stored one ...
          if (localPermissionData[i].usecase === permissionData.usecase) {

            // ... overwrite it locally. (@ToDo: Maybe some comparison f.e. with timestamps here?)
            localPermissionData[i] = permissionData;

            // Update value.
            permissionAvailable = true;

            Logger.info(this.moduleName, `updatePermissionModel`, `Successfully updated permission model entry for permissionType '${permissionData.usecase}'.`, permissionData);
          }
        }

        // After looping, no match, new entry.
        if (!permissionAvailable) {

          // Create new entry.
          localPermissionData.push(permissionData);

          Logger.info(this.moduleName, `updatePermissionModel`, `Successfully created new permission model entry for permissionType '${permissionData.usecase}'.`, permissionData);
        }
      }

      return {
        identity: null,
        data: localPermissionData,
        tcfVersion: 1,
        tcString: permissionModel.tcString // TODO: update tcString
      };

    } catch (error) {
      Logger.error(this.moduleName, `updatePermissionData`, error);
      return {
        ...permissionModel
      };
    }
  }

  protected destroy(): void {
    if (this.parentConnection) {
      this.parentConnection.destroy();
      this.parentConnection = null;
    }

    if (this.aditionService) {
      this.aditionService.destroy();
      this.aditionService = null;
    }

    this.permissionClient = null;
    this.permissionModel = null;
  }

}
